/** Übersicht tab — 1:1 z prototype/detail.jsx DetailMain overview.
 *  Levá karta Installation (mini-mapa + Adresse/Kontakt/Termin/Posádka),
 *  pravý sloupec System + Wert (gold-glow accent). */

import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Ic } from "@/components/ui/icons";
import { AddressMap } from "@/components/ui/address-map";
import { fmtEur } from "@/lib/format";
import { t } from "@/i18n/de";
import type { PartnerOrder } from "@/lib/types";
import { fetchDealPaymentSteps, type DealComplaint } from "@/lib/fetchers";
import { queryKeys } from "@/lib/queries";

interface OverviewTabProps {
  order: PartnerOrder;
  complaints: DealComplaint[];
}

export function OverviewTab({ order }: OverviewTabProps) {
  const dateObj = order.scheduled_date ? new Date(order.scheduled_date) : null;
  const value = order.value_eur || 0;
  const abSigned = order.ab_signed_at ? new Date(order.ab_signed_at) : null;

  const stepsQ = useQuery({
    queryKey: [...queryKeys.orderDetail(order.id), "payment-steps"],
    queryFn: () => fetchDealPaymentSteps(order.id),
  });
  const steps = stepsQ.data || [];

  const stepLabel = (invoiceType: string, label: string): string => {
    if (label) return label;
    if (invoiceType === "advance") return "Anzahlung";
    if (invoiceType === "final") return "Schlussrechnung";
    return invoiceType;
  };

  const stepAmount = (s: { percent: number | null; fixed_amount: number | null }): number => {
    if (s.fixed_amount != null && s.fixed_amount > 0) return Number(s.fixed_amount);
    if (s.percent != null) return value * (Number(s.percent) / 100);
    return 0;
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.2fr 1fr",
        gap: 24,
      }}
    >
      <div className="card" style={{ padding: 20 }}>
        <div
          style={{
            fontSize: 11.5,
            color: "var(--text-3)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            marginBottom: 14,
          }}
        >
          {t.detail.cards.installation}
        </div>
        <AddressMap
          street={order.installation_street}
          city={order.installation_city}
          zip={order.installation_zip}
          country={order.installation_country}
          cityLabel={order.installation_city}
        />
        <div style={{ display: "grid", gap: 14 }}>
          <Field
            label={t.detail.fields.address}
            value={
              <>
                <div>{order.installation_street || "—"}</div>
                <div>
                  {[order.installation_zip, order.installation_city].filter(Boolean).join(" ") ||
                    "—"}
                </div>
              </>
            }
          />
          <Field
            label={t.detail.fields.contact}
            value={
              <>
                <div>{order.installation_contact_name || order.end_client_name || "—"}</div>
                {order.installation_contact_phone && (
                  <div style={{ color: "var(--text-2)", fontSize: 12.5 }}>
                    <Ic.Phone size={11} /> {order.installation_contact_phone}
                  </div>
                )}
              </>
            }
          />
          <Field
            label={t.detail.fields.term}
            value={
              dateObj
                ? `${dateObj.toLocaleDateString("de-DE", { weekday: "short" })}, ${dateObj.getDate()}.${dateObj.getMonth() + 1}.${dateObj.getFullYear()}`
                : "—"
            }
          />
          <Field label={t.detail.fields.crew} value={"—"} />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="card" style={{ padding: 20 }}>
          <div
            style={{
              fontSize: 11.5,
              color: "var(--text-3)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 14,
            }}
          >
            {t.detail.cards.system}
          </div>
          <div style={{ display: "grid", gap: 14 }}>
            <Field label={t.detail.fields.systemType} value={order.system_type || "—"} />
            <Field label={t.detail.fields.maker} value={order.device_label || "—"} />
            <Field
              label={t.detail.fields.ab}
              value={
                abSigned
                  ? t.detail.fields.abSigned(
                      `${abSigned.getDate()}.${abSigned.getMonth() + 1}.${abSigned.getFullYear()}`,
                    )
                  : "—"
              }
            />
          </div>
        </div>

        <div className="card gold-glow" style={{ padding: 20, borderColor: "var(--gold-20)" }}>
          <div
            style={{
              fontSize: 11.5,
              color: "var(--gold)",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 12,
            }}
          >
            {t.detail.cards.value}
          </div>
          <div
            className="num-lg"
            style={{
              fontSize: 38,
              color: "var(--gold-hi)",
              lineHeight: 1,
            }}
          >
            {fmtEur(value)}
          </div>
          <div style={{ marginTop: 8, fontSize: 12.5, color: "var(--text-2)" }}>
            {t.detail.valueCard.vatNote}
          </div>
          <hr className="hr" style={{ margin: "16px 0" }} />
          {steps.length === 0 ? (
            <div style={{ fontSize: 12.5, color: "var(--text-3)" }}>
              {stepsQ.isLoading ? t.loading : "Kein Zahlungsschema definiert."}
            </div>
          ) : (
            steps.map((s, idx) => {
              const pct = s.percent != null ? Math.round(Number(s.percent)) : null;
              const labelText =
                s.fixed_amount && Number(s.fixed_amount) > 0
                  ? stepLabel(s.invoice_type, s.label)
                  : `${stepLabel(s.invoice_type, s.label)}${pct != null ? ` (${pct} %)` : ""}`;
              return (
                <div
                  key={s.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 13,
                    marginTop: idx === 0 ? 0 : 8,
                  }}
                >
                  <span style={{ color: "var(--text-2)" }}>{labelText}</span>
                  <span className="num">{fmtEur(stepAmount(s))}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11.5,
          color: "var(--text-3)",
          marginBottom: 4,
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13.5, color: "var(--text)" }}>{value}</div>
    </div>
  );
}
